/**
 * Configuration for country-based restrictions on trading platforms and challenges
 * This module handles regional compliance and availability restrictions
 */

import {
  ACCOUNT_SIZES,
  TRADING_PLATFORMS,
  CHALLENGE_STEPS,
} from "@/lib/constants/products";

export interface ChallengeRestriction {
  size: string;
  plan: string;
}

export interface RestrictedCountriesConfig {
  countries_by_platform: Record<string, string[]>;
  countries_by_challenges: Record<string, ChallengeRestriction[]>;
}

/**
 * Restricted countries configuration
 */
export const RESTRICTED_COUNTRIES_CONFIG: RestrictedCountriesConfig = {
  countries_by_platform: {
    [TRADING_PLATFORMS.CTRADER]: ["US", "PR", "VG", "GU", "UM", "AS"],
    [TRADING_PLATFORMS.METATRADER_5]: ["US", "CA"],
    // Example: [TRADING_PLATFORMS.MATCH_TRADE]: ['PK', 'IN'],
  },
  countries_by_challenges: {
    PK: [
      { size: ACCOUNT_SIZES.SIZE_50K, plan: CHALLENGE_STEPS.TWO_STEP },
      { size: ACCOUNT_SIZES.SIZE_100K, plan: CHALLENGE_STEPS.TWO_STEP },
    ],
    LA: [
      { size: ACCOUNT_SIZES.SIZE_50K, plan: CHALLENGE_STEPS.TWO_STEP },
      { size: ACCOUNT_SIZES.SIZE_100K, plan: CHALLENGE_STEPS.TWO_STEP },
    ],
    MM: [
      { size: ACCOUNT_SIZES.SIZE_50K, plan: CHALLENGE_STEPS.TWO_STEP },
      { size: ACCOUNT_SIZES.SIZE_100K, plan: CHALLENGE_STEPS.TWO_STEP },
    ],
    TW: [
      { size: ACCOUNT_SIZES.SIZE_50K, plan: CHALLENGE_STEPS.TWO_STEP },
      { size: ACCOUNT_SIZES.SIZE_100K, plan: CHALLENGE_STEPS.TWO_STEP },
    ],
    VN: [
      { size: ACCOUNT_SIZES.SIZE_50K, plan: CHALLENGE_STEPS.TWO_STEP },
      { size: ACCOUNT_SIZES.SIZE_100K, plan: CHALLENGE_STEPS.TWO_STEP },
    ],
  },
};

/**
 * Get restricted countries by platform
 */
export const get_countries_by_platform = (): Record<string, string[]> => {
  return RESTRICTED_COUNTRIES_CONFIG.countries_by_platform;
};

/**
 * Get restricted countries by challenges
 */
export const get_countries_by_challenges = (): Record<
  string,
  ChallengeRestriction[]
> => {
  return RESTRICTED_COUNTRIES_CONFIG.countries_by_challenges;
};

/**
 * Check if a challenge is restricted for a specific country
 * @param country_code - Country code (e.g., 'PK')
 * @param challenge_size - Challenge size (e.g., '$50k')
 * @param challenge_plan - Challenge plan (e.g., '2-step')
 * @returns {boolean} True if restricted, false otherwise
 */
export const is_challenge_restricted = (
  country_code: string,
  challenge_size: string,
  challenge_plan: string,
): boolean => {
  const restrictions =
    RESTRICTED_COUNTRIES_CONFIG.countries_by_challenges[country_code];

  if (!restrictions) {
    return false;
  }

  return restrictions.some(
    (restriction) =>
      restriction.size === challenge_size &&
      restriction.plan === challenge_plan,
  );
};

/**
 * Check if a specific trading platform is restricted for a given country
 * @param country_code - Two-letter country code (e.g., 'US', 'CA')
 * @param platform - Platform name (e.g., 'ctrader', 'metatrader-5')
 * @returns boolean - true if restricted, false if allowed
 */
export const is_platform_restricted = (
  country_code: string,
  platform: string,
): boolean => {
  const platform_restrictions =
    RESTRICTED_COUNTRIES_CONFIG.countries_by_platform[platform];

  if (!platform_restrictions) {
    return false;
  }

  return platform_restrictions.includes(country_code);
};

/**
 * Get all restricted platforms for a given country
 * @param country_code - Two-letter country code
 * @returns string[] - Array of restricted platform names
 */
export const get_restricted_platforms_by_country = (
  country_code: string,
): string[] => {
  const restricted_platforms: string[] = [];

  Object.entries(RESTRICTED_COUNTRIES_CONFIG.countries_by_platform).forEach(
    ([platform, countries]) => {
      if (countries.includes(country_code)) {
        restricted_platforms.push(platform);
      }
    },
  );

  return restricted_platforms;
};
